import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import SyncAltIcon from "@mui/icons-material/SyncAlt";
import { useDataProvider, useNotify } from "react-admin";
import { useQuery } from "react-query";

const propagationTypeOptions = [
  { value: "companies", label: "Company", note: "Global master data" },
  { value: "motifs", label: "Motif", note: "Global master data" },
  { value: "brands", label: "Brand", note: "Global master data" },
  { value: "sizes", label: "Size", note: "Global master data" },
  { value: "densities", label: "Density", note: "Global master data" },
  { value: "colors", label: "Color", note: "Global master data" },
  { value: "qualities", label: "Quality", note: "Global master data" },
  { value: "users", label: "User", note: "Company scoped" },
];

const propagationTypeMap = {
  companies: {
    resource: "companies",
    label: "Company",
    title: "Company",
    description: "Pilih company/depo yang mau disebar ke depo target.",
    itemLabel: (record) => record.code || record.name || record.id,
  },
  motifs: {
    resource: "motifs",
    label: "Motif",
    title: "Motif",
    description: "Pilih motif yang mau disebar ke depo target.",
    itemLabel: (record) =>
      record.code && record.name
        ? `${record.code} - ${record.name}`
        : record.code || record.name || record.id,
  },
  brands: {
    resource: "brands",
    label: "Brand",
    title: "Brand",
    description: "Pilih brand yang mau disebar ke depo target.",
    itemLabel: (record) =>
      record.code && record.name
        ? `${record.code} - ${record.name}`
        : record.code || record.name || record.id,
  },
  sizes: {
    resource: "sizes",
    label: "Size",
    title: "Size",
    description: "Pilih size yang mau disebar ke depo target.",
    itemLabel: (record) =>
      record.code && record.name
        ? `${record.code} - ${record.name}`
        : record.code || record.name || record.id,
  },
  densities: {
    resource: "densities",
    label: "Density",
    title: "Density",
    description: "Pilih density yang mau disebar ke depo target.",
    itemLabel: (record) =>
      record.code && record.name
        ? `${record.code} - ${record.name}`
        : record.code || record.name || record.id,
  },
  colors: {
    resource: "colors",
    label: "Color",
    title: "Color",
    description: "Pilih color yang mau disebar ke depo target.",
    itemLabel: (record) =>
      record.code && record.name
        ? `${record.code} - ${record.name}`
        : record.code || record.name || record.id,
  },
  qualities: {
    resource: "qualities",
    label: "Quality",
    title: "Quality",
    description: "Pilih quality yang mau disebar ke depo target.",
    itemLabel: (record) =>
      record.code && record.name
        ? `${record.code} - ${record.name}`
        : record.code || record.name || record.id,
  },
  users: {
    resource: "users",
    label: "User",
    title: "User",
    description: "Pilih user yang mau dipropagasi ke depo target.",
    itemLabel: (record) => {
      const names = [record.first_name, record.last_name].filter(Boolean).join(" ");
      return [record.username, names].filter(Boolean).join(" - ") || record.id;
    },
  },
};

const normalizeText = (value) => String(value || "").trim().toLowerCase();

const isHeadOfficeCompany = (company) => {
  const code = normalizeText(company?.code || company?.short_name);
  const labels = [company?.name, company?.label].map(normalizeText);

  return (
    code === "ho" ||
    labels.some(
      (value) =>
        value === "ho" ||
        ["head office", "head-office", "headoffice"].some((keyword) =>
          value.includes(keyword)
        )
    )
  );
};

const sortNewestFirst = (records) =>
  [...records].sort((a, b) => {
    const aTime = Date.parse(a?.created_at || a?.updated_at || "");
    const bTime = Date.parse(b?.created_at || b?.updated_at || "");
    if (!Number.isNaN(aTime) && !Number.isNaN(bTime) && aTime !== bTime) {
      return bTime - aTime;
    }
    return normalizeText(a?.name || a?.code || a?.id).localeCompare(
      normalizeText(b?.name || b?.code || b?.id)
    );
  });

const SyncPropagationList = () => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [propagationType, setPropagationType] = useState("motifs");
  const [selectedItemIds, setSelectedItemIds] = useState([]);
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [itemSearch, setItemSearch] = useState("");
  const [itemSearchQuery, setItemSearchQuery] = useState("");
  const [executing, setExecuting] = useState(false);
  const [result, setResult] = useState(null);

  const typeConfig = propagationTypeMap[propagationType];

  useEffect(() => {
    const timeout = setTimeout(() => {
      setItemSearchQuery(itemSearch);
    }, 300);

    return () => clearTimeout(timeout);
  }, [itemSearch]);

  const { data: itemList, isLoading: loadingItems } = useQuery(
    [propagationType, itemSearchQuery, "getList"],
    () =>
      dataProvider.getList(typeConfig.resource, {
        pagination: { page: 1, perPage: 100 },
        sort: { field: "created_at", order: "DESC" },
        filter: itemSearchQuery ? { all: itemSearchQuery } : {},
      }),
    {
      enabled: Boolean(typeConfig),
    }
  );
  const items = itemList?.data || [];

  const { data: companies = [], isLoading: loadingCompanies } = useQuery(
    ["companies/select", "getListNoParams"],
    () => dataProvider.getListNoParams("companies/select")
  );

  const selectedItems = useMemo(() => {
    const map = new Map(items.map((item) => [item.id, item]));
    return selectedItemIds.map((id) => map.get(id)).filter(Boolean);
  }, [items, selectedItemIds]);

  const filteredItems = useMemo(() => {
    const search = normalizeText(itemSearch);
    const sortedItems = sortNewestFirst(items);
    if (!search) {
      return sortedItems;
    }

    return sortedItems.filter((item) => {
      const label = typeConfig?.itemLabel ? typeConfig.itemLabel(item) : item.id;
      return normalizeText(label).includes(search);
    });
  }, [items, itemSearch, typeConfig]);

  const targetCompanies = useMemo(
    () => companies.filter((company) => !isHeadOfficeCompany(company)),
    [companies]
  );

  useEffect(() => {
    setSelectedItemIds([]);
    setSelectedCompanies([]);
    setItemSearch("");
    setItemSearchQuery("");
    setResult(null);
  }, [propagationType]);

  const handleSelectAll = () => {
    setSelectedItemIds(filteredItems.map((item) => item.id));
  };

  const handleSelectAllCompanies = () => {
    setSelectedCompanies(targetCompanies.map((company) => company.id));
  };

  const toggleItem = (itemId) => {
    setSelectedItemIds((current) =>
      current.includes(itemId)
        ? current.filter((id) => id !== itemId)
        : [...current, itemId]
    );
  };

  const handleExecute = async () => {
    if (!typeConfig) {
      notify("Tipe propagasi tidak valid.", { type: "warning" });
      return;
    }

    if (selectedItemIds.length === 0) {
      notify(`Pilih minimal satu ${typeConfig.label.toLowerCase()}.`, {
        type: "warning",
      });
      return;
    }
    if (selectedCompanies.length === 0) {
      notify("Pilih minimal satu depo target.", { type: "warning" });
      return;
    }

    setExecuting(true);
    setResult(null);

    try {
      const response = await dataProvider.simplePostJson(
        "sync/propagations/execute",
        {
          data: {
            propagation_type: propagationType,
            source_ids: selectedItemIds,
            target_company_ids: selectedCompanies,
          },
        }
      );

      setResult(response);
      setSelectedItemIds([]);
      setSelectedCompanies([]);
      setItemSearch("");
      notify("Propagasi berhasil dibuat.", { type: "info" });
    } catch (error) {
      const message =
        error?.body?.message ||
        error?.message ||
        "Propagasi gagal dijalankan.";
      setResult({ status: "error", message });
      notify(message, { type: "warning" });
    } finally {
      setExecuting(false);
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1280, mx: "auto" }}>
      <Paper variant="outlined" sx={{ p: 3 }}>
        <Stack spacing={3}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <SyncAltIcon fontSize="small" />
            <Box>
              <Typography variant="h5">Sync Propagation</Typography>
              <Typography variant="body2" color="text.secondary">
                Pilih tipe data, pilih item yang mau disebar, pilih target depo,
                lalu tekan Execute.
              </Typography>
            </Box>
          </Stack>

          <Alert severity="info">
            Halaman ini hanya tersedia untuk administrator.
          </Alert>

          <Divider />

          <Stack spacing={2}>
            <Typography variant="h6">Jenis propagasi</Typography>
            <FormControl fullWidth>
              <InputLabel id="propagation-type-label">Tipe</InputLabel>
              <Select
                labelId="propagation-type-label"
                label="Tipe"
                value={propagationType}
                onChange={(event) => setPropagationType(event.target.value)}
              >
                {propagationTypeOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Typography variant="body2" color="text.secondary">
              {typeConfig?.description}
            </Typography>
          </Stack>

          <Divider />

          <Stack spacing={2}>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Button variant="outlined" onClick={handleSelectAll}>
                Pilih semua hasil
              </Button>
              <Button variant="outlined" onClick={() => setSelectedItemIds([])}>
                Kosongkan
              </Button>
            </Stack>
            <TextField
              label={`Cari ${typeConfig?.label || "data"}`}
              value={itemSearch}
              onChange={(event) => setItemSearch(event.target.value)}
              fullWidth
            />

            {loadingItems ? (
              <Typography variant="body2" color="text.secondary">
                Memuat data...
              </Typography>
            ) : (
              <Box
                sx={{
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 1,
                  p: 2,
                  maxHeight: 380,
                  overflow: "auto",
                }}
              >
                <Stack spacing={0.5}>
                  {filteredItems.map((item) => {
                    const label = typeConfig?.itemLabel
                      ? typeConfig.itemLabel(item)
                      : item.id;
                    return (
                      <FormControlLabel
                        key={item.id}
                        control={
                          <Checkbox
                            checked={selectedItemIds.includes(item.id)}
                            onChange={() => toggleItem(item.id)}
                          />
                        }
                        label={label}
                      />
                    );
                  })}
                  {filteredItems.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      Data tidak ditemukan.
                    </Typography>
                  ) : null}
                </Stack>
              </Box>
            )}
          </Stack>

          <Divider />

          <Stack spacing={2}>
            <Typography variant="h6">Target depo</Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {propagationType !== "users" ? (
                <Button variant="outlined" onClick={handleSelectAllCompanies}>
                  Pilih semua depo
                </Button>
              ) : null}
              <Button variant="outlined" onClick={() => setSelectedCompanies([])}>
                Kosongkan depo
              </Button>
            </Stack>
            <Autocomplete
              multiple={propagationType !== "users"}
              fullWidth
              loading={loadingCompanies}
              options={targetCompanies}
              value={
                propagationType === "users"
                  ? targetCompanies.find(
                      (company) => company.id === selectedCompanies[0]
                    ) || null
                  : targetCompanies.filter((company) =>
                      selectedCompanies.includes(company.id)
                    )
              }
              onChange={(_, newValue) =>
                setSelectedCompanies(
                  propagationType === "users"
                    ? newValue?.id
                      ? [newValue.id]
                      : []
                    : newValue.map((item) => item.id)
                )
              }
              isOptionEqualToValue={(option, value) => option.id === value?.id}
              getOptionLabel={(option) => option?.name ?? ""}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Target depo"
                  placeholder={
                    propagationType === "users"
                      ? "Pilih satu depo target"
                      : "Pilih depo target"
                  }
                />
              )}
            />
            {propagationType === "users" ? (
              <Typography variant="body2" color="text.secondary">
                Propagasi user hanya dapat dikirim ke satu depo tujuan.
              </Typography>
            ) : null}
          </Stack>

          <Divider />

          <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
            <Button
              variant="contained"
              onClick={handleExecute}
              disabled={executing}
            >
              {executing ? "Menjalankan..." : "Execute"}
            </Button>
            <Typography variant="body2" color="text.secondary">
              {selectedItems.length} item dipilih, {selectedCompanies.length} depo target.
            </Typography>
          </Stack>

          {result ? (
            <Alert severity={result.status === "error" ? "error" : "success"}>
              {result.message || "Propagasi selesai."}
              {result.status !== "error" &&
              typeof result.created_count === "number"
                ? ` (${result.created_count} tracker dibuat)`
                : null}
            </Alert>
          ) : null}
        </Stack>
      </Paper>
    </Box>
  );
};

export default SyncPropagationList;
